import React, { useEffect, useState } from 'react';
import { Box, Slide } from '@mui/material';
import DeviceList from './DeviceList';
import { useOrientation } from '../hooks/useOrientation';

interface DeviceListModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  selectedHabitacion?: number | null;
  roomMatrixView: boolean;
  nombreHabitacion?: string;
  isDashboardView?: boolean;
  isTodosTab?: boolean;
  isSettingsPage?: boolean;
  onOfflineClick?: () => void;
  onTotalClick?: () => void;
  showOnlyTop10?: boolean; // Nueva prop para mostrar solo top 10
  showOnlyTop15?: boolean; // Nueva prop para mostrar solo top 15
  hideOfflineAndTotal?: boolean; // Nueva prop para ocultar recuadros Offline y Con Consumo
  excludeArtifactTypes?: string[]; // Tipos de artefacto a excluir
  includeSpecialRooms?: boolean; // Nueva prop para incluir habitaciones especiales
  excludeSpecialRoomNames?: string[]; // Nombres de habitaciones especiales a excluir específicamente
  maxHeight?: string; // Nueva prop para altura máxima personalizada
}

const DeviceListModal: React.FC<DeviceListModalProps> = ({
  isOpen,
  onClose,
  isAdmin,
  selectedHabitacion = null,
  roomMatrixView,
  nombreHabitacion = '',
  isDashboardView = false,
  isTodosTab = false,
  isSettingsPage = false,
  onOfflineClick,
  onTotalClick,
  showOnlyTop10 = false,
  showOnlyTop15 = false,
  hideOfflineAndTotal = false,
  excludeArtifactTypes = [],
  includeSpecialRooms = false,
  excludeSpecialRoomNames = [],
  maxHeight,
}) => {
  const isHorizontal = useOrientation(); // Hook para detectar orientación
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      // Delay para permitir que la animación de salida se complete
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Función para manejar el swipe down (gesto de cerrar) - solo desde el header
  const headerRef = React.useRef<HTMLDivElement>(null);
  const handleHeaderTouchStart = (event: React.TouchEvent) => {
    // Solo procesar swipe down desde el header (barra superior del modal)
    const touch = event.touches[0];
    if (!touch) return;
    
    const startY = touch.clientY;
    const startX = touch.clientX;
    
    const handleTouchMove = (moveEvent: TouchEvent) => {
      if (!moveEvent.touches[0]) return;
      
      const currentY = moveEvent.touches[0].clientY;
      const currentX = moveEvent.touches[0].clientX;
      const deltaY = currentY - startY;
      const deltaX = Math.abs(currentX - startX);
      
      // Si hay movimiento horizontal significativo, cancelar
      if (deltaX > 20) {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        return;
      }
      
      // Si el swipe es hacia abajo y es significativo (solo desde el header), cerrar el modal
      if (deltaY > 100) {
        event.preventDefault();
        onClose();
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      }
    };
    
    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
  };

  // En horizontal, siempre mostrar si está abierto (no need for isVisible state here)
  if (isHorizontal) {
    if (!isOpen) return null; // Only render if explicitly open
    return (
      <Box
        data-testid="device-list-modal"
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: '#1a1a1a',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          touchAction: 'pan-y', // Permitir scroll vertical
          // No borders or rounded corners here, as they are handled by the parent Box in App.tsx
        }}
      >
        <DeviceList
          isAdmin={isAdmin}
          selectedHabitacion={selectedHabitacion}
          roomMatrixView={roomMatrixView}
          nombreHabitacion={nombreHabitacion}
          isDashboardView={isDashboardView}
          isTodosTab={isTodosTab}
          isSettingsPage={isSettingsPage}
          onOfflineClick={onOfflineClick}
          onTotalClick={onTotalClick}
          showOnlyTop10={showOnlyTop10}
          showOnlyTop15={showOnlyTop15}
          hideOfflineAndTotal={hideOfflineAndTotal}
          excludeArtifactTypes={excludeArtifactTypes}
          includeSpecialRooms={includeSpecialRooms}
          excludeSpecialRoomNames={excludeSpecialRoomNames}
        />
      </Box>
    );
  }

  // En vertical, mostrar como modal con animación
  if (!isVisible) return null;

  return (
    <Box
      data-testid="device-list-modal"
      onClick={(e) => {
        // Cerrar solo si se hace click fuera del contenido del modal
        const target = e.target as HTMLElement;
        const modalContent = target.closest('[data-modal-content]');
        if (!modalContent) {
          onClose();
        }
      }}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: '56px',
        // Por encima del contenido de las páginas (~100–1002), debajo de la bottom nav (1400)
        zIndex: 1350,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        pointerEvents: 'auto',
      }}
    >
      {/* Backdrop - Transparente para no oscurecer el gráfico */}
      <Box
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'transparent',
          cursor: 'pointer',
          pointerEvents: 'none', // No bloquear eventos del contenido
          zIndex: 0,
        }}
      />

      {/* Modal Content con animación Slide */}
      <Slide
        direction="up"
        in={isOpen}
        timeout={300}
        mountOnEnter
        unmountOnExit
      >
        <Box
          data-modal-content
          onClick={(e) => {
            // Prevenir que el click se propague al backdrop
            e.stopPropagation();
          }}
          sx={{
            position: 'relative',
            width: '100%',
            height: maxHeight || 'calc(100vh - 120px)',
            backgroundColor: '#1a1a1a',
            borderRadius: '16px 16px 0 0',
            borderTop: '2px solid #2391FF',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 1,
            pointerEvents: 'auto',
            touchAction: 'pan-y', // Permitir scroll vertical
          }}
        >
          {/* Barra superior para swipe down (solo en esta área) */}
          <Box
            ref={headerRef}
            onTouchStart={handleHeaderTouchStart}
            sx={{
              height: '40px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              touchAction: 'pan-y',
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing',
              },
            }}
          >
            {/* Indicador visual de que se puede arrastrar */}
            <Box
              sx={{
                width: '40px',
                height: '4px',
                backgroundColor: '#666',
                borderRadius: '2px',
                mt: 1,
              }}
            />
          </Box>
          
          {/* Contenido del modal - DeviceList (igual que horizontal) */}
          <DeviceList
            isAdmin={isAdmin}
            selectedHabitacion={selectedHabitacion}
            roomMatrixView={roomMatrixView}
            nombreHabitacion={nombreHabitacion}
            isDashboardView={isDashboardView}
            isTodosTab={isTodosTab}
            isSettingsPage={isSettingsPage}
            onOfflineClick={onOfflineClick}
            onTotalClick={onTotalClick}
            showOnlyTop10={showOnlyTop10}
            showOnlyTop15={showOnlyTop15}
            hideOfflineAndTotal={hideOfflineAndTotal}
            excludeArtifactTypes={excludeArtifactTypes}
            includeSpecialRooms={includeSpecialRooms}
            excludeSpecialRoomNames={excludeSpecialRoomNames}
          />
        </Box>
      </Slide>
    </Box>
  );
};

export default DeviceListModal;